import React, { useEffect, useState } from "react";

import { Box, List, ListItem, ListItemButton, ListItemText, Typography } from "@mui/material";
import { Drawer } from "components/Measure-data/Drawer/Drawer";
import { covanentTypesToComponents, COVENANT_TYPES } from "constants/covanents";
import { useMeasuresSelector } from "store/selectors/deal-selectors/deal-selectors";
import { useDealIdSelector } from "store/selectors/deal-selectors/deal-selectors";
import { Covenant } from "store/types/deal/covanents";

import { CovenantSettingsView } from "./CovanentSettingsView";

interface AddCovenantDrawerProps {
  open: boolean;
  onClose: (changed?: boolean) => void;
  covenant?: Covenant | null;
}

interface SelectTypeViewProps {
  selectedType: string | null;
  setSelectedType: (type: string) => void;
  className?: string;
}

const SelectTypeView: React.FC<SelectTypeViewProps> = ({ setSelectedType, className }) => (
  <Box className={className}>
    <Typography variant="h6" mb={2}>
      Select covenant type
    </Typography>
    <List
      sx={{
        "& .MuiListItem-root": {
          borderBottom: "1px solid",
          borderColor: "divider",
          "&:last-child": {
            borderBottom: "none"
          }
        }
      }}
    >
      {COVENANT_TYPES.map((type) => (
        <ListItem key={type.key} disablePadding>
          <ListItemButton
            onClick={() => setSelectedType(type.key)}
            sx={{
              py: 2,
              "&:hover": {
                backgroundColor: "action.hover"
              }
            }}
          >
            <ListItemText primary={type.label} />
          </ListItemButton>
        </ListItem>
      ))}
    </List>
  </Box>
);

export const AddCovenantDrawer: React.FC<AddCovenantDrawerProps> = ({ open, onClose, covenant }) => {
  const [selectedType, setSelectedType] = useState<string | null>(covenant ? covenant.type : null);
  const measures = useMeasuresSelector();
  const dealId = useDealIdSelector();

  // If covenant changes (edit mode), update selectedType
  useEffect(() => {
    if (covenant) {
      setSelectedType(covenant ? covenant.type : null);
    }
  }, [covenant]);

  return (
    <Drawer
      anchor="left"
      open={open}
      handleClose={() => onClose(false)}
      title={covenant ? "Covenant settings" : "Add covenant"}
      PaperProps={{ sx: { width: 520, borderRadius: "0 20px 20px 0" } }}
    >
      <div style={{ width: 520, padding: 24 }}>
        {selectedType ? (
          <CovenantSettingsView
            covenant={
              covenant ||
              ({
                type: selectedType,
                deal_id: dealId,
                name: COVENANT_TYPES.find((type) => type.key === selectedType)?.label ?? "",
                components: covanentTypesToComponents[selectedType as keyof typeof covanentTypesToComponents] ?? {}
              } as Covenant)
            }
            onSuccess={() => onClose(true)}
            measures={measures}
            isEdit={!!covenant}
            onBack={() => setSelectedType(null)}
          />
        ) : (
          <SelectTypeView selectedType={selectedType} setSelectedType={setSelectedType} />
        )}
      </div>
    </Drawer>
  );
};
